import { map, catchError } from 'rxjs/operators';
import {
  throwError as observableThrowError,
  Observable,
  from as fromPromise,
  BehaviorSubject,
  of as observableOf
} from 'rxjs';

import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SessionCookieService } from '@app/core/cookies';
import * as localForage from 'localforage';
import * as fromUser from '@app/core/user/store/reducers';

import {
  User,
  UsersService,
  SessionsService,
  ResetPasswordRequestsService,
  Session,
  ContentsService,
  Product,
  WelcomeVoucher
} from '@vestiaire/api-sdk-angular';
import { AuthModal } from '@app/shared/model/interfaces/AuthDialog/AuthModal';
import { IAppState } from '@app/store';
import { select, Store } from '@ngrx/store';
import { UserTransactionRole } from '@app/core/user/models/types';

@Injectable()
export class UserService {
  private authDialogData: BehaviorSubject<AuthModal> = new BehaviorSubject<AuthModal>({
    authModalType: 'login',
    credentials: { email: '' }
  });

  constructor(
    private usersService: UsersService,
    private sessionsService: SessionsService,
    private resetPasswordRequestsService: ResetPasswordRequestsService,
    private sessionCookieService: SessionCookieService,
    private contentsService: ContentsService,
    private store: Store<IAppState>,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Find a user by Id.
   *
   * @param id unique user Id.
   * @returns found user or null.
   */
  findUser(id: string): Observable<User> {
    return this.usersService
      .getUser(id)
      .pipe(map(res => res.data))
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Get the currently authenticated user.
   *
   * @returns found user or null.
   */
  getAuthenticatedUser(): Observable<User> {
    return this.store.pipe(select(fromUser.getUser));
  }

  /**
   * Retrieve the authenticated user from session data.
   *
   * @todo work on housing user in local storage to improve performances.
   *
   * @returns found user or null.
   */
  getUserFromSession(): Observable<User> {
    if (isPlatformBrowser(this.platformId) && this.sessionCookieService.hasSession()) {
      const userId = this.sessionCookieService.getUserId();

      if (typeof userId !== 'string') {
        return observableThrowError(new Error('Not current user Id found'));
      }

      return this.findUser(userId);
    }
    return observableOf(null);
  }

  /**
   * Returns the role of a user in a transaction.
   *
   * @param user the user for whom to get role
   * @param product the product related to transaction
   *
   * @returns
   */
  getUserNegotiationRole(user: User, product: Product): UserTransactionRole {
    return user.id === product.seller.id ? 'seller' : 'buyer';
  }

  isPremium(user: User): boolean {
    return user && user.badges && user.badges.includes('premium');
  }

  /**
   * Returns public info about of persons followed by a user
   *
   * @param id
   * @returns
   */
  getUserFollows(id: string): Observable<any> {
    return this.usersService
      .getFollowers(id)
      .pipe(map(res => res.data))
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Returns public info about of followers of a user
   *
   * @param id
   * @returns
   */
  getUserFollowers(id: string): Observable<any> {
    return this.usersService
      .getFollowers(id)
      .pipe(map(res => res.data))
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Returns public info about of persons followed by a user
   *
   * @param id
   * @returns
   */
  getUserViewedProducts(id: string): Observable<any> {
    return this.usersService
      .getViewedProducts(id)
      .pipe(map(res => res.data))
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Returns settings
   *
   * @returns
   */
  getUserSettings(): Observable<any> {
    return this.usersService
      .getUserSettings()
      .pipe(map(res => res.data))
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Update settings
   *
   * @param i18n
   * @returns
   */
  updateUserSettings(i18n: any): Observable<any> {
    return this.usersService
      .updateUserSettings(i18n)
      .pipe(map(res => res.data))
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Retrieve current user's wallet
   *
   * @returns
   */
  getUserWallet(): Observable<any> {
    return this.usersService
      .getWallet()
      .pipe(map(res => res.data))
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Retrieve current user's likes
   *
   * @returns
   */
  getUserLikes(): Observable<any> {
    return this.usersService
      .getLikes()
      .pipe(map(res => res.data))
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Returns the currently active welcome voucher depending
   * on whether user is authenticated or not.
   *
   * @param authenticated `true` if user is logged in
   *
   * @returns current welcome voucher
   */
  getWelcomeVoucher(authenticated?: boolean): Observable<WelcomeVoucher> {
    if (authenticated) {
      return this.usersService.getWelcomeVoucher().pipe(map(res => res.data));
    }
    return fromPromise(this.getOfflineWelcomeVoucher() as Promise<WelcomeVoucher>);
  }

  /**
   * Retrieve offline user voucher
   * TODO: Modifier le any par une interface Voucher
   */
  getOfflineWelcomeVoucher() {
    const item = 'welcomeVoucher';

    return localForage
      .getItem(item)
      .then(async (voucher: { data: any; expirationDate: Date }) => {
        if (!voucher) {
          try {
            const result = await this.contentsService.getOfflineWelcomeVoucher().toPromise();

            const expirationDate = new Date();
            expirationDate.setHours(expirationDate.getHours() + 6);

            await localForage.setItem(item, {
              data: result.data,
              expirationDate
            });
            return result.data;
          } catch (error) {
            return null;
          }
        } else if (voucher.expirationDate < new Date()) {
          try {
            await localForage.removeItem(item);
            return this.getOfflineWelcomeVoucher();
          } catch (error) {
            return null;
          }
        } else {
          return voucher.data;
        }
      })
      .catch(() => {
        return null;
      });
  }

  /**
   * Register a new user
   *
   * @returns
   */
  registerUser(params: any): Observable<Session> {
    return this.usersService
      .registerUser(params)
      .pipe(map(res => res.data))
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Log-in (create a new session)
   *
   * @param urlRequestParams Credentials
   * @returns the created session
   */
  login(urlRequestParams: any): Observable<Session> {
    return this.sessionsService
      .createSession(urlRequestParams)
      .pipe(map(res => res.data))
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Delete an user session (log-out)
   *
   * @returns
   */
  logout(): Observable<any> {
    const sessionId = this.sessionCookieService.getSessionId();
    return this.sessionsService
      .deleteSession(sessionId)
      .pipe(map(res => res))
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Send an e-mail to the user if the email exist
   *
   * @param email
   * @returns
   */
  resetUserPassword(email: string) {
    return this.resetPasswordRequestsService
      .requestPasswordReset({ email })
      .pipe(catchError((error: any) => observableThrowError(error)));
  }

  /**
   * Update / set user to LocalStorage
   *
   * @param user
   */
  setUserToLocalStorage(user: any) {
    // In the following line, you should include the prefixes of implementations you want to test.
    localForage.getItem('user').then(currentUser => {
      if (!currentUser) {
        localForage.setItem('user', user).then(() => {});
      }
    });
  }

  /**
   * Return user from LocalStorage
   */
  getUserFromLocalStorage(): Observable<User> {
    return fromPromise(localForage.getItem('user') as Promise<User>);
  }

  setAuthDialogData(data: AuthModal): void {
    this.authDialogData.next(data);
  }

  getAuthDialogData(): Observable<AuthModal> {
    return this.authDialogData;
  }

  /**
   * Recently Seen Products
   */
  getRecentlySeenProducts(limit: number = 12): Promise<Product[]> {
    return localForage
      .getItem('recentlySeenProducts')
      .then((products: Array<Product>) => {
        if (products) {
          return products.slice(0, limit);
        } else {
          return [];
        }
      })
      .catch(err => {
        return [];
      });
  }

  addRecentlySeenProduct(product: Product) {
    localForage
      .getItem('recentlySeenProducts')
      .then(async (products: Array<Product>) => {
        if (!products) {
          localForage
            .setItem('recentlySeenProducts', [product])
            .then(() => {})
            .catch(() => {});
        } else {
          const existingProductIndex = products.findIndex(item => item.id === product.id);
          if (existingProductIndex > -1) {
            products.splice(existingProductIndex, 1);
          }
          products.unshift(product);
          localForage
            .setItem('recentlySeenProducts', products)
            .then(() => {})
            .catch(() => {});
        }
      })
      .catch(() => {});
  }
}
